import { z } from 'zod';
import { RegenerationProposalDecisionSchema, RegenerationProposalKindSchema } from '../enums.js';
import { IdSchema, IsoDateTimeSchema } from './common.js';
import { GeneratedTaskDraftSchema } from './task.js';
/** projects/{id}/regenerations/{id}/proposals/{id} — one per task, classified by the LLM after
 * comparing the old and new document version. See docs/PLAN.md §6. */
export const RegenerationProposalSchema = z.object({
    id: IdSchema,
    kind: RegenerationProposalKindSchema,
    taskId: IdSchema.nullable(), // null for "new" proposals until accepted
    reason: z.string().max(1000),
    /** Only the LLM-owned fields (see REGENERATION_OWNED_TASK_FIELDS) — never status, assignee,
     * actuals, comments, issues or attachments. */
    patch: z.record(z.string(), z.unknown()).nullable().default(null),
    newTask: GeneratedTaskDraftSchema.nullable().default(null),
    conflictsWithHumanEdit: z.boolean().default(false),
    decision: RegenerationProposalDecisionSchema.default('pending'),
    decidedByUid: IdSchema.nullable().default(null),
    decidedAt: IsoDateTimeSchema.nullable().default(null),
});
/** projects/{id}/regenerations/{id} */
export const RegenerationSchema = z.object({
    id: IdSchema,
    trigger: z.enum(['prd_accepted', 'tdd_accepted']),
    fromVersionId: IdSchema,
    toVersionId: IdSchema,
    status: z.enum(['pending', 'in_review', 'completed']).default('pending'),
    createdAt: IsoDateTimeSchema,
    proposalCounts: z.object({
        affected: z.number().int().nonnegative(),
        new: z.number().int().nonnegative(),
        obsolete: z.number().int().nonnegative(),
    }),
});
export const DecideRegenerationProposalInputSchema = z.object({
    decision: z.enum(['accepted', 'rejected']),
});
/** What the LLM returns when classifying the impact of a document change on existing tasks —
 * see docs/PLAN.md §6. Re-validated against this schema before any proposal is written; taskId
 * references and patch fields are further checked against the real task list server-side
 * (routes/regenerations.ts) since the model may hallucinate either. */
export const RegenerationClassificationItemSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('affected'),
        taskId: IdSchema,
        reason: z.string().min(1).max(1000),
        /** Only fields that should change to stay in sync with the new document — narrowed
         * server-side to REGENERATION_OWNED_TASK_FIELDS regardless of what is returned here. */
        patch: z.record(z.string(), z.unknown()).default({}),
    }),
    z.object({
        kind: z.literal('obsolete'),
        taskId: IdSchema,
        reason: z.string().min(1).max(1000),
    }),
    z.object({
        kind: z.literal('new'),
        reason: z.string().min(1).max(1000),
        newTask: GeneratedTaskDraftSchema,
    }),
]);
export const RegenerationClassificationResultSchema = z.array(RegenerationClassificationItemSchema);
//# sourceMappingURL=regeneration.js.map